import { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { setBanner } from 'lib/notification/actions'
import { httpGet } from 'lib/http'
import { useDebounce } from '../../../../lib/hooks/index.js'

export const useFeeData = (form, initialFeeData = null, updateForm = null) => {
  const dispatch = useDispatch()

  const [feeData, setFeeData] = useState({
    fee_amount: null,
    balance_snapshot_at: null,
    enrollment_fee_amount: null,
    calculated_enrollment_fee_amount: null,
    fee_rate: null,
    fee_schedule: null,
    main_balance: null,
    subaccounts_balance: null,
    total_balance_snapshot: null,
  })

  const [isLoadingFees, setIsLoadingFees] = useState(false)
  const [userHasClickedCheckbox, setUserHasClickedCheckbox] = useState(false)

  const [initialAccountNumber, setInitialAccountNumber] = useState(null)
  const [initialAmount, setInitialAmount] = useState(null)
  const [initialIsRefund, setInitialIsRefund] = useState(null)
  const [initialIsTransfer, setInitialIsTransfer] = useState(null)
  const [initialTakeEnrollmentFee, setInitialTakeEnrollmentFee] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [hasInitialFeeData, setHasInitialFeeData] = useState(false)

  const hasInitialized = useRef(false)
  const debouncedAmount = useDebounce(form.amount, 500)
  const shouldClearFees = form.deposit_to_cash === true || (!form.trust_account && !form.account)

  const fetchFees = async (accountNumber, amount) => {
    if (!accountNumber || !amount || parseFloat(amount) <= 0) {
      setFeeData({
        fee_amount: null,
        balance_snapshot_at: null,
        enrollment_fee_amount: null,
        calculated_enrollment_fee_amount: null,
        fee_rate: null,
        fee_schedule: null,
        main_balance: null,
        subaccounts_balance: null,
        total_balance_snapshot: null,
      })
      setIsLoadingFees(false)
      return
    }

    setIsLoadingFees(true)

    try {
      const isRefundOrTransfer = form.is_refund === true || form.is_transfer === true
      const takeEnrollmentFee = form.take_enrollment_fee === true
      const depositId = form.deposit_id || null

      const queryParams = `?number=${encodeURIComponent(accountNumber)}&amount=${encodeURIComponent(
        parseFloat(amount),
      )}&is_refund_or_transfer=${encodeURIComponent(isRefundOrTransfer)}&take_enrollment_fee=${encodeURIComponent(takeEnrollmentFee)}&id=${encodeURIComponent(depositId)}`

      const response = await httpGet(`/admin/deposits/fees${queryParams}`)

      let responseData = null
      if (response && response.data && response.data.fees) {
        responseData = response.data.fees
      } else if (response && response.fees) {
        responseData = response.fees
      } else if (response) {
        responseData = {
          fee_amount: response.fee_amount || null,
          balance_snapshot_at: response.balance_snapshot_at || null,
          enrollment_fee_amount: response.enrollment_fee_amount || null,
          calculated_enrollment_fee_amount: response.calculated_enrollment_fee_amount || null,
          fee_rate: response.fee_rate || null,
          fee_schedule: response.fee_schedule || null,
          main_balance: response.main_balance || null,
          subaccounts_balance: response.subaccounts_balance || null,
          total_balance_snapshot: response.total_balance_snapshot || null,
        }
      }

      if (responseData) {
        setFeeData(responseData)

        const calculatedFee = parseFloat(responseData.calculated_enrollment_fee_amount) || 0

        if (!userHasClickedCheckbox && updateForm) {
          const shouldBeChecked = calculatedFee > 0

          updateForm({ target: { name: 'take_enrollment_fee', value: shouldBeChecked } })
        }
      }
    } catch (error) {
      if (error.status === 400) {
        dispatch(setBanner('Invalid parameters for fee calculation. Please check account number and amount.', 'danger'))
      } else if (error.status === 404) {
        dispatch(setBanner('Fee calculation service not found', 'danger'))
      } else {
        dispatch(setBanner(`Error calculating fees: ${error.message || 'Unknown error'}`, 'danger'))
      }
    } finally {
      setIsLoadingFees(false)
    }
  }

  const handleEnrollmentFeeChange = (value) => {
    setUserHasClickedCheckbox(true)
    if (updateForm) {
      updateForm({ target: { name: 'take_enrollment_fee', value } })
    }
  }

  useEffect(() => {
    if (shouldClearFees) {
      setFeeData({
        fee_amount: null,
        balance_snapshot_at: null,
        enrollment_fee_amount: null,
        calculated_enrollment_fee_amount: null,
        fee_rate: null,
        fee_schedule: null,
        main_balance: null,
        subaccounts_balance: null,
        total_balance_snapshot: null,
      })
      setIsLoadingFees(false)
    }
  }, [shouldClearFees])

  useEffect(() => {
    if (initialFeeData && !hasInitialized.current) {
      setHasInitialFeeData(true)

      if (!shouldClearFees) {
        setFeeData({
          fee_amount: initialFeeData.fee_amount || null,
          balance_snapshot_at: initialFeeData.balance_snapshot_at || null,
          enrollment_fee_amount: initialFeeData.enrollment_fee_amount || null,
          calculated_enrollment_fee_amount: initialFeeData.calculated_enrollment_fee_amount || null,
          fee_rate: initialFeeData.fee_rate || null,
          fee_schedule: initialFeeData.fee_schedule || null,
          main_balance: initialFeeData.main_balance_snapshot || null,
          subaccounts_balance: initialFeeData.subaccounts_balance_snapshot || null,
          total_balance_snapshot: initialFeeData.total_balance_snapshot || null,
        })

        const calculatedEnrollmentFee = parseFloat(initialFeeData.calculated_enrollment_fee_amount || 0)

        if (calculatedEnrollmentFee > 0 && form.take_enrollment_fee === true && updateForm) {
          updateForm({ target: { name: 'take_enrollment_fee', value: true } })
        } else if (form.take_enrollment_fee === false) {
          updateForm({ target: { name: 'take_enrollment_fee', value: false } })
        }

        if (form.take_enrollment_fee === true) {
          setUserHasClickedCheckbox(true)
        }
      }

      setIsLoadingFees(false)

      const accountNumber = form.trust_account?.number || form.account?.number
      setInitialAccountNumber(accountNumber)
      setInitialAmount(form.amount)
      setInitialIsRefund(form.is_refund)
      setInitialIsTransfer(form.is_transfer)
      setInitialTakeEnrollmentFee(form.take_enrollment_fee)
      setIsInitialized(true)
      hasInitialized.current = true
    } else if (!initialFeeData && !hasInitialized.current) {
      setHasInitialFeeData(false)

      const accountNumber = form.trust_account?.number || form.account?.number
      setInitialAccountNumber(accountNumber)
      setInitialAmount(form.amount)
      setInitialIsRefund(form.is_refund)
      setInitialIsTransfer(form.is_transfer)
      setInitialTakeEnrollmentFee(form.take_enrollment_fee)
      setIsInitialized(true)
      hasInitialized.current = true
      setIsLoadingFees(false)
    }
  }, [initialFeeData, shouldClearFees, form.take_enrollment_fee, updateForm])

  useEffect(() => {
    if (!isInitialized || shouldClearFees) {
      return
    }

    const currentAccountNumber = form.trust_account?.number || form.account?.number
    const currentAmount = debouncedAmount
    const currentIsRefund = form.is_refund
    const currentIsTransfer = form.is_transfer
    const currentTakeEnrollmentFee = form.take_enrollment_fee

    const accountChanged = currentAccountNumber !== initialAccountNumber
    const amountChanged = currentAmount !== initialAmount
    const refundChanged = currentIsRefund !== initialIsRefund
    const transferChanged = currentIsTransfer !== initialIsTransfer
    const enrollmentFeeChanged = currentTakeEnrollmentFee !== initialTakeEnrollmentFee

    if (enrollmentFeeChanged && !accountChanged && !amountChanged && !refundChanged && !transferChanged) {
      setUserHasClickedCheckbox(true)
    }

    const shouldFetchFees =
      (accountChanged || amountChanged || refundChanged || transferChanged || enrollmentFeeChanged) && currentAccountNumber && currentAmount

    if (shouldFetchFees) {
      fetchFees(currentAccountNumber, currentAmount)

      setInitialAccountNumber(currentAccountNumber)
      setInitialAmount(currentAmount)
      setInitialIsRefund(currentIsRefund)
      setInitialIsTransfer(currentIsTransfer)
      setInitialTakeEnrollmentFee(currentTakeEnrollmentFee)
    }
  }, [
    form.trust_account?.number,
    form.account?.number,
    debouncedAmount,
    form.is_refund,
    form.is_transfer,
    form.take_enrollment_fee,
    initialAccountNumber,
    initialAmount,
    initialIsRefund,
    initialIsTransfer,
    initialTakeEnrollmentFee,
    isInitialized,
    shouldClearFees,
  ])

  return {
    feeData,
    isLoadingFees,
    handleEnrollmentFeeChange,
  }
}
